import React, { useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import cx from 'classnames';
import useTranslation from 'next-translate/useTranslation';
import Button from '../../ui/button';
import useLinkProps from '../../../hooks/use-link-props';
import FeaturedLink from '../../ui/links/featured';
import IconPlus from '../../ui/icons/plus';
import useIsMobile from '../../../hooks/use-is-mobile';
import Chevrelon from '../../ui/icons/cheverlon';
import Link from 'next/link';

type PartsCloudProps = {
  groups: any;
  groupsSort?: any;
};

const PartsCloud: React.FC<PartsCloudProps> = ({ groups, groupsSort }) => {
  const { push } = useRouter();
  const getLink = useLinkProps();
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  const [toggledState, setToggledState] = useState<boolean | null>(null);
  const isOpen = toggledState !== null ? toggledState : !isMobile;

  const partsToDisplay = useMemo(() => {
    const list: { name: string; slug: string; categorySlug: string }[] = [];
    const sourceGroups = groups || groupsSort;
    if (sourceGroups && Array.isArray(sourceGroups)) {
      sourceGroups.forEach((group: any) => {
        if (group.parts) {
          Object.values(group.parts).forEach((subgroup: any) => {
            if (Array.isArray(subgroup)) {
              subgroup.forEach((part: any) => {
                if (part.name && part.slug) {
                  list.push({ name: part.name, slug: part.slug, categorySlug: part.subgroup_slug });
                }
              });
            }
          });
        }
      });
    }

    const uniqueMap = new Map();
    list.forEach((part) => uniqueMap.set(part.slug, part));
    let totalToDisplay = Array.from(uniqueMap.values()).sort((a, b) =>
      a.name.localeCompare(b.name)
    );
    //return totalToDisplay.slice(0, 15)
    return totalToDisplay;
  }, [groups, groupsSort]);

  if (!partsToDisplay || partsToDisplay.length === 0) {
    return null;
  }

  return (
    <div className="container mx-auto w-full flex flex-col items-center justify-center pb-8 gap-6">
      <Button
        variant={isOpen ? 'ROUNDED' : 'GHOST'}
        onClick={() => setToggledState(!isOpen)}
        className={cx(
          'flex flex-row items-center justify-center gap-2 !px-6 !py-3 transition-colors',
          isOpen
            ? 'text-white !bg-neutral-80 hover:!bg-neutral-50 rounded-full'
            : '!text-blue-100 hover:!bg-blue-10 rounded-full'
        )}
        isFullWidth={false}
        disableHeight={true}
      >
        <span className="font-bold text-[18px]">
          {t('categories:bestSellingParts')}
        </span>
        {isOpen ? (
          <IconPlus isMinus={false} className="w-5 h-5 rotate-45" />
        ) : (
          <Chevrelon className="w-5 h-5 flex-shrink-0" />
        )}
      </Button>

      {isOpen && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-x-2 gap-y-2 w-full px-4 lg:px-0 justify-items-center items-center">
            {partsToDisplay.map((part) => (
              <Link
                {...getLink('search', {
                  category: part.categorySlug,
                  subCategory: part.slug,
                })}
                passHref
                shallow
                scroll
                key={part.slug}
                variant="GHOST"
                className="w-full hover:!bg-brand-10 !px-4 !py-2 !text-neutral-100 transition-colors text-center !h-auto !min-h-12 !font-normal flex items-center justify-center"
                isFullWidth
              >
                {part.name}
              </Link>
            ))}
          </div>

          <FeaturedLink
            as="button"
            variant="blackBold"
            onClick={() => {
              const link = getLink('categories', {});
              push(link.href, link.as, { shallow: true });
            }}
            className="mt-4"
          >
            {t('categories:seeAllParts')}
          </FeaturedLink>
        </>
      )}
    </div>
  );
};

export default PartsCloud;
